import { GridRowsProp } from '@mui/x-data-grid';
import { ProjectTableDataType, ProjectTableType } from './types';
import { Lesson } from '../../api/types/lesson';
import { ContentGroup } from '../../api/types/contentGroup';
import { getLessonStatusLabel } from '../../api/lesson/utils';
import { getDisplayNames } from '../../api/utils';
import { FavoriteState, Deal, LikeState, PlaybackState, SubscriptionInterval } from '../../api/types/others';
import { Author } from '../../api/types/author';
import { Category, Goal, Pillar, Promotion, AuthorCategory } from '../../api/types/contentManagement';
import { FormQuestion } from '../../api/types/forms';
import { Purchase, Transaction } from '../../api/types/profile';
import { PUBLIC_BUCKET } from '../../defines';
import { Campaign } from '../../api/types/campaign';
import { LessonStats } from '../../api/types/lessonStats';
import { DailyMissionTemplate, LevelDefinition } from '../../api/types/progress';
import { MentorBoard } from '../../api/types/mentorBoard';
import { MentorShort } from '../../api/types/mentorShort';

const getSearchValue = (values: (string | undefined)[]): string | undefined => {
  return values && values?.length > 0 ? values?.reduce((result, item) => result?.concat(' ', item || '')) : undefined;
};

export const parseDataToRows = (type: ProjectTableType, data: ProjectTableDataType): GridRowsProp => {
  switch (type) {
    case 'lessons':
    case 'lessonsPreview':
    case 'lessonsInCollection':
      return (data as Lesson[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          category: item?.category,
          pillarType: getDisplayNames.pillarType(item?.pillarType),
          pillarExpertise: getDisplayNames.pillarExpertise(item?.pillarExpertise),
          status: getLessonStatusLabel(item?.status),
          publishedAt: item?.publishedAt,
          searchValue: getSearchValue([item?.title, item?.id]),
          rowIndex,
          rowsNum,
          fullContentData: item,
        };
      });

    case 'collections':
    case 'collectionsPreview':
    case 'collectionsInBundle':
    case 'bundles':
    case 'bundlesPreview':
      return (data as ContentGroup[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          category: item?.category,
          pillarType: getDisplayNames.pillarType(item?.pillarType),
          pillarExpertise: getDisplayNames.pillarExpertise(item?.pillarExpertise),
          publishedAt: item?.publishedAt,
          searchValue: getSearchValue([item?.title, item?.id]),
          rowIndex,
          rowsNum,
          fullContentData: item,
        };
      });

    case 'profileSubscriptionIntervals':
      return (data as SubscriptionInterval[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: data.indexOf(item),
          purchaseDate: item?.purchaseDate,
          expirationDate: item?.expirationDate,
          rowIndex,
          rowsNum,
        };
      });

    case 'profileSessionPlayback':
      return (data as PlaybackState[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.linkedContentId,
          title: item?.linkedContentTitle,
          type: getDisplayNames.contentType(item?.linkedContentType) || null,
          isFinished: item?.isFinished ? 'Yes' : 'No',
          userProgress: Math.round(item?.progress),
          totalLength: Math.round(item?.totalLength),
          progressPercent:
            item?.progress && item?.totalLength ? Math.round((item?.progress / item?.totalLength) * 100) : null,
          updateDate: item?.updatedAt,
          rowIndex,
          rowsNum,
        };
      });

    case 'profileContentRating':
      return (data as LikeState[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.linkedContentId,
          title: item?.linkedContentTitle,
          type: getDisplayNames.contentType(item?.linkedContentType) || null,
          rating: item?.isLike ? 'Like' : 'Dislike',
          updateDate: item?.updatedAt,
          rowIndex,
          rowsNum,
        };
      });

    case 'profileLinkedContentBasic':
      return (data as FavoriteState[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.linkedContentId,
          title: item?.linkedContentTitle,
          type: getDisplayNames.contentType(item?.linkedContentType) || null,
          updateDate: item?.updatedAt,
          rowIndex,
          rowsNum,
        };
      });

    case 'profileDeals':
    case 'profileDealsPreview':
      return (data as Deal[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: `${data.indexOf(item)}`,
          type: getDisplayNames.dealType(item?.type),
          context: item?.context,
          end: item?.endAt,
          rowIndex,
          rowsNum,
        };
      });

    case 'basicSingleText':
      return (data as string[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: data.indexOf(item),
          text: item,
          rowIndex,
          rowsNum,
        };
      });

    case 'campaigns':
      return (data as Campaign[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          createdAt: item?.createdAtDate,
          updatedAt: item?.updatedAtDate,
          offering: item?.offering,
          active: item?.isDisabled ? 'Inactive' : 'Active',
          branchUrl: item?.branchUrl,
          searchValue: getSearchValue([item?.id, item?.name]),
          rowIndex,
          rowsNum,
        };
      });

    case 'authors':
      return (data as Author[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          imagePath: item?.imagePath,
          imageUrl: PUBLIC_BUCKET + item?.imagePath,
          initials: item?.initials,
          bio: item?.bio,
          category: (item as any)?.category || 'No Category',
          isActive: item?.isActive !== false,
          coursesCount: (item as any)?.coursesCount || 0,
          lessonsCount: (item as any)?.lessonsCount || 0,
          qaCount: (item as any)?.qaCount || 0,
          shortsCount: (item as any)?.shortsCount || 0,
          releaseDate: item?.releaseDate,
          searchValue: getSearchValue([item?.id, item?.name]),
          rowIndex,
          rowsNum,
        };
      });

    case 'categories':
      return (data as Category[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          searchValue: getSearchValue([item?.id, item?.name]),
          rowIndex,
          rowsNum,
        };
      });

    case 'authorCategories':
      return (data as AuthorCategory[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          searchValue: getSearchValue([item?.id, item?.name]),
          rowIndex,
          rowsNum,
        };
      });

    case 'goals':
    case 'goalsInSections':
    case 'goalsPreview':
      return (data as Goal[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          linkedPillars: item?.linkedPillars?.map((item) => getDisplayNames.pillarType(item))?.join(', '),
          linkedCategories: item?.linkedCategories?.join(', '),
          searchValue: getSearchValue([item?.id, item?.name]),
          rowIndex,
          rowsNum,
        };
      });

    case 'mentorShorts':
      return (data as (MentorShort & { authorName?: string })[])?.map((item, index) => ({
        id: item?.id,
        title: item?.title,
        authorName: item?.authorName ?? item?.authorId,
        durationSeconds: item?.durationSeconds,
        isActive: item?.isActive,
        searchValue: getSearchValue([item?.title, item?.authorName, item?.id]),
        rowIndex: index,
        rowsNum: data.length,
      }));

    case 'dailyMissionTemplates':
      return (data as DailyMissionTemplate[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          targetType: item?.targetType,
          targetValue: item?.targetValue,
          order: item?.order,
          isActive: item?.isActive,
          searchValue: getSearchValue([item?.title, item?.id]),
          rowIndex,
          rowsNum,
        };
      });

    case 'levelDefinitions':
      return (data as LevelDefinition[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          level: item?.level,
          title: item?.title,
          minPoints: item?.minPoints,
          searchValue: getSearchValue([item?.title, String(item?.level)]),
          rowIndex,
          rowsNum,
        };
      });

    case 'mentorBoards':
      return (data as MentorBoard[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          subtitle: item?.subtitle,
          isActive: item?.isActive,
          memberCount: item?.memberAuthorIds?.length || 0,
          searchValue: getSearchValue([item?.title, item?.id]),
          rowIndex,
          rowsNum,
        };
      });

    case 'promotions':
      return (data as Promotion[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          offerId: item?.offerId,
          startDate: item?.startDate,
          endDate: item?.endDate,
          searchValue: getSearchValue([item?.title]),
          rowIndex,
          rowsNum,
        };
      });

    case 'reflectionQuestions':
      return (data as FormQuestion[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          text: item?.text,
          quoteAuthor: item?.extraData?.quoteAuthor,
          quoteText: item?.extraData?.quoteText,
          searchValue: getSearchValue([item?.id, item?.text]),
          rowIndex,
          rowsNum,
        };
      });

    case 'journalQuestions':
      return (data as FormQuestion[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          text: item?.text,
          questionType: item?.responses?.length === 2 ? 'Two response fields' : 'Single free text response',
          searchValue: getSearchValue([item?.id, item?.text]),
          rowIndex,
          rowsNum,
        };
      });

    case 'pillarSurveyQuestions':
      return (data as FormQuestion[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          text: item?.text,
          key: item?.key,
          pillar: getDisplayNames.pillarType(item?.extraData?.pillar),
          searchValue: getSearchValue([item?.id, item?.text]),
          rowIndex,
          rowsNum,
        };
      });

    case 'pillars':
      return (data as Pillar[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: getDisplayNames.pillarType(item?.name),
          rowIndex,
          rowsNum,
        };
      });

    case 'lessonStats':
      return (data as LessonStats[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          totalScore: item?.totalScore,
          recentScore: item?.recentScore,
          totalActionCounts: item?.totalActionCounts,
          recentActionCounts: item?.recentActionCounts,
          lastUpdated: item?.lastUpdated,
          title: item?.title,
          pillarType: getDisplayNames.pillarType(item?.pillarType),
          pillarExpertise: getDisplayNames.pillarExpertise(item?.pillarExpertise),
          searchValue: getSearchValue([item?.id, item?.title]),
          rowIndex,
          rowsNum,
        };
      });

    case 'purchases':
    case 'transactions':
      return (data as (Purchase | Transaction)[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          context: item?.context,
          status: item?.status,
          productId: item?.productId,
          createdAt: item?.createdAt,
          rowIndex,
          rowsNum,
        };
      });

    case 'networks':
      return (data as any[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          name: item?.name,
          networkId: item?.networkId,
          searchValue: getSearchValue([item?.id, item?.name, item?.networkId]),
          rowIndex,
          rowsNum,
        };
      });

    case 'userPaths':
      return (data as any[])?.map((item, index) => {
        const rowIndex = index;
        const rowsNum = data.length;
        return {
          id: item?.id,
          title: item?.title,
          paths: item?.paths,
          searchValue: getSearchValue([item?.id, item?.title]),
          rowIndex,
          rowsNum,
        };
      });

    default:
      return [];
  }
};
